import type { RunPage } from "contracts/api/crawl/run-read.responses";
import { CRAWL_FRONTIER_STATUS } from "contracts/enums/core";
import type { RunFailureKind } from "../../enums/runs";
import { RUN_FAILURE_KIND } from "../../enums/runs";

/**
 * What a page-level run failure says to the user: which hop broke, and what to
 * do about it. Copy only — the value sets it keys on are contract and frontend
 * enums, so a new failure reason fails to compile until it is worded here.
 */

type FailureKind = RunFailureKind;

type PageFailurePresentation = {
  kind: FailureKind;
  detail: string;
};

const ASSESSMENT_FAILURE_COPY: Record<
  NonNullable<RunPage["attemptFailureReason"]>,
  string
> = {
  boot_failed: "Faro's assessment process could not start for this page.",
  no_mcp_contact: "Faro's assessment process could not connect for this page.",
  agent_error: "Faro's assessment process stopped before completing this page.",
  timeout: "Faro's assessment process timed out on this page.",
  budget_exhausted: "The assessment did not finish within this run's limits.",
};

const PAGE_FAILURE_COPY: Record<string, PageFailurePresentation> = {
  fetch_failed: {
    kind: RUN_FAILURE_KIND.CAPTURE,
    detail: "Faro could not capture a usable page response.",
  },
  provider_failed: {
    kind: RUN_FAILURE_KIND.CAPTURE,
    detail: "Faro could not capture a usable page response.",
  },
  capture_contract_failed: {
    kind: RUN_FAILURE_KIND.CAPTURE,
    detail: "Faro could not capture a usable page response.",
  },
  extract_failed: {
    kind: RUN_FAILURE_KIND.CAPTURE,
    detail: "Faro captured the page but could not prepare it for assessment.",
  },
  artifact_store_failed: {
    kind: RUN_FAILURE_KIND.CAPTURE,
    detail: "Faro could not store the captured page evidence.",
  },
  aborted: {
    kind: RUN_FAILURE_KIND.INCOMPLETE,
    detail: "The scan ended before this page completed.",
  },
  attempts_exceeded: {
    kind: RUN_FAILURE_KIND.INCOMPLETE,
    detail: "Faro could not complete this page within the available attempts.",
  },
};

export function pageFailurePresentation(
  page: RunPage,
): PageFailurePresentation | null {
  if (page.attemptFailureReason) {
    return {
      kind: RUN_FAILURE_KIND.ASSESSMENT,
      detail: ASSESSMENT_FAILURE_COPY[page.attemptFailureReason],
    };
  }
  const pageFailure = page.failureReason
    ? PAGE_FAILURE_COPY[page.failureReason]
    : null;
  if (pageFailure) return pageFailure;
  if (page.status === CRAWL_FRONTIER_STATUS.FAILED) {
    return {
      kind: RUN_FAILURE_KIND.INCOMPLETE,
      detail: "Faro could not complete this page in the current run.",
    };
  }
  return null;
}
